import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { createAlterationSchema, parseRupees, type OrderItem } from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import { useCreateAlteration } from '../../lib/alterations/use-alterations.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Booking a garment back in.
 *
 * Three fields, and the order they are in is the order the conversation happens in: what is
 * wrong, when they can have it, what it costs. The charge is LAST and defaults to blank —
 * meaning free — because most re-fits are, and a form that led with a price would make the
 * counter decide about money before it had understood the problem.
 */

export interface BookAlterationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: OrderItem | null;
}

export function BookAlterationDialog({
  open,
  onOpenChange,
  item,
}: BookAlterationDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();
  const create = useCreateAlteration();

  const [complaint, setComplaint] = useState('');
  const [promisedDate, setPromisedDate] = useState('');
  const [charge, setCharge] = useState('');
  const [notes, setNotes] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Reopening for a different garment must not show the previous one's complaint.
  useEffect(() => {
    if (!open) return;
    setComplaint('');
    setPromisedDate('');
    setCharge('');
    setNotes('');
    setFieldErrors({});
    setFormError(null);
  }, [open, item]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!item) return;
    setFieldErrors({});
    setFormError(null);

    const payload = {
      orderItemId: item.id,
      complaint,
      promisedDate: promisedDate || null,
      // Blank means free, which is what most re-fits are.
      chargePaise: charge.trim() === '' ? 0 : parseRupees(charge),
      notes: notes.trim() === '' ? null : notes,
    };

    const parsed = createAlterationSchema.safeParse(payload);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[issue.path.join('.')] ??= issue.message;
      setFieldErrors(next);
      return;
    }

    try {
      await create.mutateAsync(payload);
      toast({ title: t('alterations.booked'), variant: 'success' });
      onOpenChange(false);
    } catch (caught) {
      setFormError(isApiRequestError(caught) ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('alterations.bookTitle')}</DialogTitle>
          <DialogDescription>
            {item
              ? t('alterations.bookDescription', {
                  garment: `${item.quantity} × ${item.garmentTypeLabel}`,
                })
              : ''}
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          {formError ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {formError}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-complaint`}>{t('alterations.field.complaint')}</Label>
            <Input
              id={`${fieldId}-complaint`}
              value={complaint}
              onChange={(event) => setComplaint(event.target.value)}
              placeholder={t('alterations.field.complaintPlaceholder')}
              aria-invalid={fieldErrors.complaint ? true : undefined}
            />
            {fieldErrors.complaint ? (
              <p className="text-sm text-destructive">{fieldErrors.complaint}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-promised`}>{t('alterations.field.promisedDate')}</Label>
              <Input
                id={`${fieldId}-promised`}
                type="date"
                value={promisedDate}
                onChange={(event) => setPromisedDate(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-charge`}>{t('alterations.field.charge')}</Label>
              <Input
                id={`${fieldId}-charge`}
                inputMode="decimal"
                value={charge}
                onChange={(event) => setCharge(event.target.value)}
                placeholder={t('alterations.field.chargePlaceholder')}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-notes`}>{t('alterations.field.notes')}</Label>
            <Textarea
              id={`${fieldId}-notes`}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {t('alterations.bookAction')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
